import { Component, OnDestroy, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { Subscription } from 'rxjs';
import {StripeService} from '../../services/stripe/stripe.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SnackBarComponent} from '../snackbar/snackbar.component';
import {TeamsService} from '../../services/teams/teams.service';
import {NativeTeamContextService} from '../../services/native-team-context/native-team-context.service';

@Component({
  selector: 'app-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss']
})
export class BillingComponent implements OnInit, OnDestroy {

  private allLinks = [
    { title: 'Payment Methods', link: '/billing/stripepaymentmethods'},
    { title: 'Add Available Payment Method', link: '/billing/stripeavailablepaymentmethods'},
    { title: 'Add Payment Method', link: '/billing/stripecreditcard'},
    { title: 'Charge History', link: '/billing/stripechargehistory'},
  ];
  public links: any[] = [];
  public activeLink: any;
  public badgeCount = 0;
  public balance = 0;
  public isNativeTeamContextActive = false;
  public nativeTeams: any[] = [];
  public nativeTeamContextReady = false;
  public balanceLoading = false;
  public balanceLoadError = '';
  public currentWellspaceId = '';
  public selectedTeamId = '';
  private balanceRequestVersion = 0;
  private wellspaceSubscription?: Subscription;
  private queryParamsSubscription?: Subscription;
  private pendingAchSubscription?: Subscription;
  private nativeTeamContextSubscription?: Subscription;
  private hasReceivedInitialPendingAchCount = false;

  constructor(
      private router: Router,
      private stripeService: StripeService,
      private route: ActivatedRoute,
      private snackBar: MatSnackBar,
      private teamsService: TeamsService,
      private nativeTeamContextService: NativeTeamContextService,
  ) { }

  async ngOnInit(): Promise<void> {
    const nativeTeamState = await this.nativeTeamContextService.initialize();
    this.applyNativeTeamState(nativeTeamState);
    this.nativeTeamContextReady = true;
    this.nativeTeamContextSubscription = this.nativeTeamContextService
      .stateChanges()
      .subscribe(state => this.applyNativeTeamState(state));

    this.wellspaceSubscription = this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any) => {
      const teamId = this.getValidatedTeamId(wellspace);
      this.clearBillingPresentationState();
      if (!teamId) {
        return;
      }
      this.currentWellspaceId = teamId;
      this.selectedTeamId = teamId;
      this.links = wellspace.role ? [this.allLinks[3]] : this.allLinks;
      this.activeLink = this.links.find(
        (link: { link: string; }) => link.link === this.router.url.split('?')[0]
      );
      void this.loadBalance(teamId);
    });
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      if (params.teamName) {
        this.snackBar.openFromComponent(SnackBarComponent, {data: `Payment method added to ${params.teamName}`});
      }
    });
    this.pendingAchSubscription = this.stripeService.pendingACHCount.subscribe(count => {
      this.badgeCount = count;
      if (!this.hasReceivedInitialPendingAchCount) {
        this.hasReceivedInitialPendingAchCount = true;
        return;
      }
      void Promise.resolve().then(() => {
        const teamId = this.currentWellspaceId;
        if (teamId && !this.balanceLoading) {
          void this.loadBalance(teamId);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.balanceRequestVersion++;
    this.wellspaceSubscription?.unsubscribe();
    this.queryParamsSubscription?.unsubscribe();
    this.pendingAchSubscription?.unsubscribe();
    this.nativeTeamContextSubscription?.unsubscribe();
  }

  get selectedNativeTeam(): any | undefined {
    return this.nativeTeams.find(team => `${team?.id || ''}`.trim() === this.selectedTeamId);
  }

  onNativeTeamChange(teamId: string): void {
    const normalizedTeamId = `${teamId || ''}`.trim();
    if (
      normalizedTeamId === this.selectedTeamId
      || !this.nativeTeams.some(team => `${team?.id || ''}`.trim() === normalizedTeamId)
    ) {
      return;
    }
    this.clearBillingPresentationState();
    this.selectedTeamId = normalizedTeamId;
    void this.nativeTeamContextService.selectTeam(normalizedTeamId);
  }

  private applyNativeTeamState(state: any): void {
    this.isNativeTeamContextActive = state.active === true;
    this.nativeTeams = this.isNativeTeamContextActive && Array.isArray(state.teams)
      ? [...state.teams]
      : [];
  }

  private getValidatedTeamId(wellspace: any): string {
    const teamId = `${wellspace?.id || ''}`.trim();
    if (!teamId) {
      return '';
    }
    if (
      this.isNativeTeamContextActive
      && !this.nativeTeams.some(team => `${team?.id || ''}`.trim() === teamId)
    ) {
      return '';
    }
    return teamId;
  }

  private clearBillingPresentationState(): void {
    this.balanceRequestVersion++;
    this.currentWellspaceId = '';
    this.selectedTeamId = '';
    this.links = [];
    this.activeLink = undefined;
    this.balance = 0;
    this.balanceLoading = false;
    this.balanceLoadError = '';
  }

  private async loadBalance(teamId: string): Promise<void> {
    if (!teamId || this.currentWellspaceId !== teamId) {
      return;
    }
    const requestVersion = ++this.balanceRequestVersion;
    this.balanceLoading = true;
    this.balanceLoadError = '';
    try {
      const balance = await this.stripeService.getCustomerBalance();
      if (
        requestVersion !== this.balanceRequestVersion
        || this.currentWellspaceId !== teamId
      ) {
        return;
      }
      this.balance = balance / 100;
    } catch (error) {
      if (
        requestVersion !== this.balanceRequestVersion
        || this.currentWellspaceId !== teamId
      ) {
        return;
      }
      this.balanceLoadError = 'Unable to load the balance for this Team.';
    } finally {
      if (
        requestVersion === this.balanceRequestVersion
        && this.currentWellspaceId === teamId
      ) {
        this.balanceLoading = false;
      }
    }
  }

}
